import moment from 'moment';

// Session data type
export interface SessionData {
  session_id: string;
  timestamp: string;
  last_message: string;
  display_name?: string; // Generated or user-edited name
  is_pinned?: boolean; // Whether session is pinned
}

// Categorized sessions type (excludes pinned - those are handled separately)
export interface CategorizedSessions {
  today: SessionData[];
  yesterday: SessionData[];
  lastWeek: SessionData[];
  older: SessionData[];
}

// Max pinned sessions allowed (frontend-only limit)
export const MAX_PINNED_SESSIONS = 10;

/**
 * Get the full title for a session (without truncation).
 * Used for tooltips/hover text.
 */
export const getSessionFullTitle = (session: SessionData): string => {
  return session.display_name || session.last_message || 'Untitled Session';
};

/**
 * Get the display title for a session.
 * Uses display_name if available, falls back to truncated last_message.
 */
export const getSessionDisplayTitle = (session: SessionData, maxLength = 40): string => {
  const title = getSessionFullTitle(session);
  return title.length > maxLength ? `${title.slice(0, maxLength)}...` : title;
};

/**
 * Separates pinned sessions from unpinned sessions.
 * @param sessions Array of session data
 * @returns Object with pinned and unpinned arrays
 */
export const separatePinnedSessions = (
  sessions: SessionData[],
): { pinned: SessionData[]; unpinned: SessionData[] } => {
  const pinned: SessionData[] = [];
  const unpinned: SessionData[] = [];

  sessions.forEach((session) => {
    if (session.is_pinned) {
      pinned.push(session);
    } else {
      unpinned.push(session);
    }
  });

  // Sort pinned by timestamp (newest first)
  pinned.sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf());

  return { pinned, unpinned };
};

/**
 * Categorizes sessions by date: Today, Yesterday, Last Week, and Older
 * Note: Should be called with unpinned sessions only (pinned are shown separately)
 * @param sessions Array of session data
 * @returns Object containing sessions categorized by date
 */
export const categorizeSessions = (sessions: SessionData[]): CategorizedSessions => {
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const lastWeekStart = moment().subtract(7, 'days').startOf('day');

  const categorized: CategorizedSessions = {
    today: [],
    yesterday: [],
    lastWeek: [],
    older: [],
  };

  sessions.forEach((session) => {
    const sessionDate = moment(session.timestamp);

    if (sessionDate.isSameOrAfter(today)) {
      categorized.today.push(session);
    } else if (sessionDate.isSameOrAfter(yesterday)) {
      categorized.yesterday.push(session);
    } else if (sessionDate.isSameOrAfter(lastWeekStart)) {
      categorized.lastWeek.push(session);
    } else {
      categorized.older.push(session);
    }
  });

  // Sort each category by timestamp (newest first)
  ['today', 'yesterday', 'lastWeek', 'older'].forEach((category) => {
    categorized[category as keyof CategorizedSessions].sort(
      (a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf(),
    );
  });

  return categorized;
};
